import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { ComplaintTrend } from '../../api';

interface TrendsChartProps {
  trends: ComplaintTrend[];
  isLoading?: boolean;
}

/**
 * Format date for display on x-axis
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Calculate summary statistics from trend data
 */
function calculateTrendStats(trends: ComplaintTrend[]): { total: number; resolved: number; trend: 'up' | 'down' | 'neutral'; change: number } {
  if (trends.length === 0) {
    return { total: 0, resolved: 0, trend: 'neutral', change: 0 };
  }

  const total = trends.reduce((sum, t) => sum + t.total, 0);
  const resolved = trends.reduce((sum, t) => sum + t.resolved, 0);

  // Calculate week-over-week trend
  const midPoint = Math.floor(trends.length / 2);
  const firstHalf = trends.slice(0, midPoint).reduce((sum, t) => sum + t.total, 0);
  const secondHalf = trends.slice(midPoint).reduce((sum, t) => sum + t.total, 0);

  let trend: 'up' | 'down' | 'neutral' = 'neutral';
  let change = 0;

  if (firstHalf > 0) {
    change = Math.round(((secondHalf - firstHalf) / firstHalf) * 100);
    if (change > 5) trend = 'up';
    else if (change < -5) trend = 'down';
  }

  return { total, resolved, trend, change: Math.abs(change) };
}

/**
 * Custom tooltip component
 */
function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg p-3 min-w-[140px]">
      <p className="text-xs font-medium text-gray-500 mb-2">{label}</p>
      <div className="space-y-1.5">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-xs text-gray-600">{entry.name}</span>
            </div>
            <span className="text-sm font-semibold text-gray-900">{entry.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TrendsChart({ trends, isLoading }: TrendsChartProps) {
  const stats = calculateTrendStats(trends);

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="h-4 bg-gray-200 rounded w-40 mb-2 animate-pulse" />
            <div className="h-3 bg-gray-200 rounded w-24 animate-pulse" />
          </div>
          <div className="h-8 bg-gray-200 rounded w-20 animate-pulse" />
        </div>
        <div className="h-64 flex items-center justify-center">
          <div className="w-full h-48 bg-gradient-to-t from-gray-100 to-gray-50 rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  if (trends.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Complaints Over Time</h3>
        <p className="text-xs text-gray-500 mb-4">Track submission and resolution trends</p>
        <div className="h-64 flex flex-col items-center justify-center text-gray-400">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            <TrendingUp className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-sm font-medium">No trend data available</p>
          <p className="text-xs mt-1">Data will appear as complaints are submitted</p>
        </div>
      </div>
    );
  }

  // Format data for chart
  const chartData = trends.map((trend) => ({
    ...trend,
    dateDisplay: formatDate(trend.date),
  }));

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
      {/* Header with stats */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Complaints Over Time</h3>
          <p className="text-xs text-gray-500">
            {stats.total} submitted · {stats.resolved} resolved
          </p>
        </div>
        {stats.trend !== 'neutral' && (
          <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
            stats.trend === 'up' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'
          }`}>
            {stats.trend === 'up' ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            <span>{stats.change}%</span>
          </div>
        )}
        {stats.trend === 'neutral' && stats.total > 0 && (
          <div className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            <Minus className="w-3 h-3" />
            <span>Stable</span>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#DE1D3B" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#DE1D3B" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis
              dataKey="dateDisplay"
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              dy={8}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              width={30}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="top"
              align="left"
              height={36}
              iconType="circle"
              iconSize={8}
              formatter={(value) => <span className="text-xs text-gray-600 ml-1">{value}</span>}
              wrapperStyle={{ paddingBottom: '8px' }}
            />
            <Area
              type="monotone"
              dataKey="total"
              name="Total Submitted"
              stroke="#DE1D3B"
              strokeWidth={2.5}
              fill="url(#colorTotal)"
              dot={{ r: 0 }}
              activeDot={{ r: 5, fill: '#DE1D3B', stroke: '#fff', strokeWidth: 2 }}
            />
            <Area
              type="monotone"
              dataKey="resolved"
              name="Resolved"
              stroke="#10b981"
              strokeWidth={2.5}
              fill="url(#colorResolved)"
              dot={{ r: 0 }}
              activeDot={{ r: 5, fill: '#10b981', stroke: '#fff', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
